/**
 * §14.1 / §14.3 / ADR-018 — Marka ve kelime normalizasyonu.
 *
 * Şartname marka eşleşmesini "büyük/küçük harf duyarsız, boşluk/noktalama normalize edilmiş
 * TAM eşleşme" olarak tanımlar. PostgreSQL `citext` yalnızca harf durumunu çözer, boşluk ve
 * noktalamayı çözmez — bu yüzden normalize edilmiş bir kolon tutup onun üzerinden eşleştiriyoruz.
 */

/** Unicode noktalama ve sembolleri (Japonca dahil) yakalar. */
const PUNCT_AND_SYMBOLS = /[\p{P}\p{S}]+/gu;
const WHITESPACE = /\s+/gu;
/**
 * `"İ".toLowerCase()` "i" + U+0307 veriyor: Türkçe klavyeyle yazılan "NİKE", "Nike" ile
 * eşleşmiyordu. Birleşik harfler (ż, ė) NFKC ile tek karakter olduğu için etkilenmiyor.
 */
const BIRLESEN_NOKTA = /̇/gu;

/**
 * Marka adını karşılaştırılabilir hale getirir.
 * `"  Sony-Ericsson®  "` → `"sonyericsson"`
 */
export function normalizeBrand(input: string): string {
  return input
    .normalize('NFKC')
    .toLowerCase()
    .replace(BIRLESEN_NOKTA, '')
    .replace(PUNCT_AND_SYMBOLS, '')
    .replace(WHITESPACE, '')
    .trim();
}

/**
 * Yasaklı kelimeyi karşılaştırılabilir hale getirir.
 * Marka normalizasyonundan farkı: **boşluk korunur**, çünkü çok kelimeli ifadelerde
 * ("apple watch") kelime sırası anlamlıdır.
 */
export function normalizeKeyword(input: string): string {
  return input
    .normalize('NFKC')
    .toLowerCase()
    .replace(BIRLESEN_NOKTA, '')
    .replace(WHITESPACE, ' ')
    .trim();
}

/** Başlığı token'lara ayırırken kullanılır (§14.3). */
export function normalizeTitle(input: string): string {
  return input.normalize('NFKC').toLowerCase().replace(BIRLESEN_NOKTA, '');
}

const CJK = /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}]/u;

/** Japonca/Çince karakter içeriyor mu? İçeriyorsa kelime sınırı yerine substring aranır. */
export function containsCjk(input: string): boolean {
  return CJK.test(input);
}

/**
 * ASIN biçim doğrulaması (§13.1): `B` + 9 alfanümerik, veya 10 haneli ISBN.
 *
 * ISBN biçimi şart: kullanıcının mağazasındaki 20 kitap ASIN'i `B0` ile
 * başlamıyor (`158779800X` gibi). Yalnızca `B` desenini kabul etseydik
 * kitaplar hiç eklenemezdi.
 */
const ASIN_RE = /^(B[0-9A-Z]{9}|[0-9]{9}[0-9X])$/;

/** ISBN-13 — kitap listelerinde ASIN yerine bu geçebiliyor. */
const ISBN13_RE = /^97[89][0-9]{10}$/;

/**
 * Girdiyi ASIN karşılaştırmasına hazırlar.
 *
 * Tire ve boşluk siliniyor: kitap kodları `978-1-4838-5122-2` gibi yazılıyor
 * ve Excel'den kopyalanan hücrelerde de boşluk kalabiliyor. Bunları
 * reddetmek, kullanıcının elindeki listeyi elle temizlemesi demekti.
 *
 * ⚠️ Yalnızca rakam-tire-boşluk içeren girdilerde temizlik yapılıyor.
 * Alfanümerik ASIN'lerde tire anlamlı olabilir; oradan bir şey silmek
 * var olmayan bir ASIN uydurmak olurdu.
 */
function normalizeAsinCandidate(raw: string): string {
  const t = raw.trim().toUpperCase();
  return /^[0-9X\s-]+$/.test(t) ? t.replace(/[\s-]/g, '') : t;
}

/**
 * ISBN-13'ü ISBN-10'a çevirir — Amazon kitaplarda ASIN olarak ISBN-10
 * kullanıyor.
 *
 * Dönüşüm belirlenimci: `978` önekli her ISBN-13'ün tek bir ISBN-10
 * karşılığı var. `979` öneklilerin ISBN-10 karşılığı **yok**; onlar
 * çevrilmeden bırakılıyor (uydurmak, var olmayan bir ürün eklemek olurdu).
 */
export function isbn13ToIsbn10(input: string): string | null {
  const digits = normalizeAsinCandidate(input);
  if (!ISBN13_RE.test(digits) || !digits.startsWith('978')) return null;

  const govde = digits.slice(3, 12);
  let toplam = 0;
  for (let i = 0; i < 9; i++) {
    toplam += Number(govde[i]) * (10 - i);
  }
  const kalan = (11 - (toplam % 11)) % 11;
  const kontrol = kalan === 10 ? 'X' : String(kalan);
  return govde + kontrol;
}

export function isValidAsin(input: string): boolean {
  return ASIN_RE.test(normalizeAsinCandidate(input));
}

/**
 * Serbest metinden ASIN'i ayıklar (URL de olabilir). Bulamazsa `null`.
 *
 * Sıra: doğrudan ASIN → ISBN-13 çevrimi → URL içinden.
 */
export function extractAsin(input: string): string | null {
  const trimmed = normalizeAsinCandidate(input);
  if (isValidAsin(trimmed)) return trimmed;

  const isbn10 = isbn13ToIsbn10(trimmed);
  if (isbn10 !== null) return isbn10;

  const fromUrl = /\/(?:DP|GP\/PRODUCT|PRODUCT)\/(B[0-9A-Z]{9}|[0-9]{9}[0-9X])/.exec(
    input.trim().toUpperCase(),
  );
  return fromUrl?.[1] ?? null;
}
